/**
 * Tipi del modulo Email Pipeline.
 *
 * Le tabelle email_* non sono ancora in database.types.ts (che si rigenera da
 * Supabase): finche' non lo saranno, i tipi di riga stanno qui e le query
 * usano il cast `.from('email_contacts' as any)`, come fa gia' leadService.
 *
 * Una nota sui colori: refactor-checks.sh fa fallire il deploy se trova classi
 * blue/indigo/sky/cyan/teal. Le tinte usate qui sotto sono tutte ammesse.
 */

/** Stato di un contatto. Lo calcola il database, l'app lo legge e basta. */
export type EmailContactStatus =
  | 'in_attesa'
  | 'attivo'
  | 'disiscritto'
  | 'bounce'
  | 'reclamo'
  | 'soppresso';

/** Su quale base giuridica gli stiamo scrivendo. */
export type EmailConsentBasis =
  | 'double_opt_in'
  | 'opt_in'
  | 'legittimo_interesse'
  | 'soft_spam'
  | 'importato';

/** Ogni riga del registro dei consensi. */
export type EmailConsentAction =
  | 'richiesta_opt_in'
  | 'conferma_double_opt_in'
  | 'opt_in_diretto'
  | 'legittimo_interesse'
  | 'soft_spam'
  | 'importazione_massiva'
  | 'opt_out'
  | 'reclamo_spam'
  | 'bounce_permanente';

/** Basi ammesse quando si importa una lista. */
export type EmailImportBasis = Extract<
  EmailConsentAction,
  'opt_in_diretto' | 'legittimo_interesse' | 'soft_spam' | 'importazione_massiva'
>;

export type EmailEntityType = 'lead' | 'customer' | 'supplier';

export interface EmailContact {
  id: string;
  email: string;
  email_normalized: string;
  first_name: string | null;
  full_name: string | null;
  company_name: string | null;
  phone: string | null;
  city: string | null;
  province: string | null;
  language: string;
  custom_fields: Record<string, unknown>;
  status: EmailContactStatus;
  consent_basis: EmailConsentBasis | null;
  consent_at: string | null;
  confirmed_at: string | null;
  unsubscribe_token: string;
  unsubscribed_at: string | null;
  unsubscribe_reason: string | null;
  bounce_count: number;
  complaint_count: number;
  last_sent_at: string | null;
  last_opened_at: string | null;
  last_clicked_at: string | null;
  last_replied_at: string | null;
  source: string | null;
  created_at: string;
  updated_at: string;
}

export interface EmailContactLink {
  id: string;
  contact_id: string;
  entity_type: EmailEntityType;
  entity_id: string;
  created_at: string;
}

export interface EmailConsent {
  id: string;
  contact_id: string;
  action: EmailConsentAction;
  source: string | null;
  source_detail: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
  privacy_version: string | null;
  privacy_text_snapshot: string | null;
  occurred_at: string;
  created_by: string | null;
}

export interface EmailTag {
  id: string;
  name: string;
  kind: 'interna' | 'categoria';
  description: string | null;
  color: string | null;
}

/**
 * Una lista di Mailing, con i suoi numeri. Nel database e' un'etichetta
 * (email_tags): nella pagina si chiama lista perche' e' cosi' che la si usa.
 */
export interface EmailLista extends EmailTag {
  created_at: string;
  /** Quanti contatti ci sono dentro, compresi quelli che non possono ricevere. */
  contatti: number;
  possono_ricevere: number;
}

/** Un contatto dell'elenco, con le liste in cui si trova. */
export interface EmailContactConListe extends EmailContact {
  liste: string[];
}

/** Cosa torna dall'importazione di una lista. */
export interface EmailImportResult {
  nuovi: number;
  aggiornati: number;
  scartati: number;
  bloccati: number;
}

/** Una riga pronta per l'importazione, cosi' come la vuole la funzione a database. */
export interface EmailImportRow {
  email: string;
  nome?: string;
  azienda?: string;
  telefono?: string;
  citta?: string;
  provincia?: string;
}

interface StatoDescritto {
  label: string;
  /** Spiegazione in chiaro, mostrata come suggerimento. */
  spiegazione: string;
  badge: string;
  puntino: string;
}

export const EMAIL_CONTACT_STATUS: Record<EmailContactStatus, StatoDescritto> = {
  in_attesa: {
    label: 'In attesa',
    spiegazione: 'Non ha ancora dato il consenso: non riceve campagne.',
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    puntino: 'bg-amber-500',
  },
  attivo: {
    label: 'Attivo',
    spiegazione: 'Puo\' ricevere le campagne.',
    badge: 'bg-green-50 text-green-700 border-green-200',
    puntino: 'bg-green-500',
  },
  disiscritto: {
    label: 'Disiscritto',
    spiegazione: 'Ha chiesto di non ricevere piu\' niente. Non rientra da solo.',
    badge: 'bg-neutral-100 text-neutral-600 border-neutral-200',
    puntino: 'bg-neutral-400',
  },
  bounce: {
    label: 'Indirizzo inesistente',
    spiegazione: 'Le mail rimbalzano: la casella non esiste o e\' chiusa.',
    badge: 'bg-orange-50 text-orange-700 border-orange-200',
    puntino: 'bg-orange-500',
  },
  reclamo: {
    label: 'Segnalato come spam',
    spiegazione: 'Ha premuto "segnala spam". Scrivergli ancora danneggia il dominio.',
    badge: 'bg-red-50 text-red-700 border-red-200',
    puntino: 'bg-red-500',
  },
  soppresso: {
    label: 'Bloccato',
    spiegazione: 'Indirizzo in lista di blocco: nessun invio, in nessun caso.',
    badge: 'bg-neutral-200 text-neutral-700 border-neutral-300',
    puntino: 'bg-neutral-600',
  },
};

export const EMAIL_CONSENT_BASIS_LABEL: Record<EmailConsentBasis, string> = {
  double_opt_in: 'Doppia conferma',
  opt_in: 'Consenso diretto',
  legittimo_interesse: 'Legittimo interesse',
  soft_spam: 'Cliente gia\' acquisito',
  importato: 'Importato senza prova',
};

export const EMAIL_CONSENT_ACTION_LABEL: Record<EmailConsentAction, string> = {
  richiesta_opt_in: 'Mail di conferma inviata',
  conferma_double_opt_in: 'Iscrizione confermata',
  opt_in_diretto: 'Consenso raccolto',
  legittimo_interesse: 'Legittimo interesse dichiarato',
  soft_spam: 'Cliente gia\' acquisito',
  importazione_massiva: 'Importato da lista',
  opt_out: 'Disiscritto',
  reclamo_spam: 'Segnalato come spam',
  bounce_permanente: 'Indirizzo inesistente',
};

/**
 * Le basi utilizzabili importando una lista, con la spiegazione di quando si
 * puo' usare ciascuna. Il testo e' pensato per chi carica il file, non per un
 * giurista: serve a far scegliere quella giusta.
 */
export const EMAIL_IMPORT_BASIS: ReadonlyArray<{
  value: EmailImportBasis;
  label: string;
  quando: string;
}> = [
  {
    value: 'opt_in_diretto',
    label: 'Hanno dato il consenso',
    quando:
      'Si sono iscritti loro, o hanno firmato un modulo in cui acconsentivano a ricevere materiale commerciale. Serve poterlo dimostrare.',
  },
  {
    value: 'soft_spam',
    label: 'Sono gia\' clienti',
    quando:
      'Hanno gia\' comprato da noi e scriviamo solo di prodotti simili a quelli acquistati, con la disiscrizione in ogni mail.',
  },
  {
    value: 'legittimo_interesse',
    label: 'Aziende, indirizzi di reparto',
    quando:
      'Indirizzi aziendali tipo info@ o acquisti@, contattati per proposte pertinenti alla loro attivita\', con disiscrizione e informativa in ogni mail.',
  },
  {
    value: 'importazione_massiva',
    label: 'Non ho una prova',
    quando:
      'I contatti entrano ma restano fermi: potranno ricevere soltanto la mail che chiede il consenso, niente campagne.',
  },
] as const;

// ---------------------------------------------------------------------------
// Campagne
// ---------------------------------------------------------------------------

export type EmailCampaignStatus =
  | 'bozza'
  | 'programmata'
  | 'in_invio'
  | 'inviata'
  | 'sospesa'
  | 'annullata'
  | 'errore';

/**
 * A chi va una campagna. 'da_scegliere' e' lo stato di partenza: finche'
 * nessuno sceglie, il database non la lascia partire.
 */
export type EmailCampaignPubblico = 'da_scegliere' | 'tutti' | 'liste';

export interface EmailCampaign {
  id: string;
  name: string;
  subject: string;
  preheader: string | null;
  document: unknown;
  from_name: string | null;
  from_email: string | null;
  reply_to: string | null;
  status: EmailCampaignStatus;
  pubblico: EmailCampaignPubblico;
  segment_id: string | null;
  tag_filter: string[] | null;
  scheduled_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  target_count: number;
  sent_count: number;
  delivered_count: number;
  open_count: number;
  click_count: number;
  bounce_count: number;
  complaint_count: number;
  unsubscribe_count: number;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface EmailCampaignPreparata {
  destinatari: number;
  esclusi_per_consenso: number;
}

export interface EmailSaluteLista {
  inviate_30gg: number;
  consegnate_30gg: number;
  rimbalzate_30gg: number;
  perc_rimbalzi: number | null;
  reclami_30gg: number;
  perc_reclami: number | null;
}

interface StatoCampagnaDescritto {
  label: string;
  spiegazione: string;
  badge: string;
  accento: string;
}

export const EMAIL_CAMPAIGN_STATUS: Record<EmailCampaignStatus, StatoCampagnaDescritto> = {
  bozza: {
    label: 'Bozza',
    spiegazione: 'Si puo\' ancora modificare tutto. Non e\' partito niente.',
    badge: 'bg-neutral-100 text-neutral-600 border-neutral-200',
    accento: 'bg-neutral-400',
  },
  programmata: {
    label: 'Programmata',
    spiegazione: 'Partira\' da sola all\'ora indicata. Fino ad allora si puo\' fermare.',
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    accento: 'bg-amber-500',
  },
  in_invio: {
    label: 'In invio',
    spiegazione: 'Sta partendo. Con il piano gratuito si spedisce a scaglioni giornalieri.',
    badge: 'bg-primary-50 text-primary-700 border-primary-200',
    accento: 'bg-primary-500',
  },
  inviata: {
    label: 'Inviata',
    spiegazione: 'Tutti i destinatari sono stati serviti.',
    badge: 'bg-green-50 text-green-700 border-green-200',
    accento: 'bg-green-500',
  },
  sospesa: {
    label: 'Sospesa',
    spiegazione: 'Fermata a meta\'. Si puo\' riprendere.',
    badge: 'bg-orange-50 text-orange-700 border-orange-200',
    accento: 'bg-orange-500',
  },
  annullata: {
    label: 'Annullata',
    spiegazione: 'Non partira\' piu\'.',
    badge: 'bg-neutral-100 text-neutral-500 border-neutral-200',
    accento: 'bg-neutral-300',
  },
  errore: {
    label: 'Errore',
    spiegazione: 'Il motore si e\' fermato su un problema. Guarda il dettaglio.',
    badge: 'bg-red-50 text-red-700 border-red-200',
    accento: 'bg-red-500',
  },
};
